/* eslint-disable @typescript-eslint/ban-types */

/**
 * Get the string tag of a value.
 * @private
 *
 * @param  value
 */
const getTag = value => toString.call(value);
/**
 * Normalizes a given `uid`. Used to handle edge case UID's such as `home`.
 * @private
 *
 * @returns The normalized UID.
 */


const normalizeUID = uid => uid === 'home' ? null : uid;
/**
 * Helper that returns the provided path without the leading or trailing `/`.
 * @private
 *
 * @param path The path to sanitize.
 * @returns The sanitized path.
 */

const sanitizePath = path => path.replace(/(^\/|\/$)/g, '');
/**
 * Returns true if given value is an instance of Map or Set.
 * @private
 *
 * @param value Value to check.
 */

const isMapOrSet = value => {
  const tag = getTag(value);
  return tag === '[object Set]' || tag === '[object Map]';
};
/**
 * Determines whether the passed object is a plain object.
 *
 * @param value The value to check.
 *
 * @returns true if value is a plain object, false otherwise.
 */


const isPlainObject = value => isMapOrSet(value) ? false : value !== null && typeof value === 'object' && !Array.isArray(value);
/**
 * Generates a unique ID. If `prefix` is given, the generated ID is appended to it.
 *
 * @param prefix The string to optionally prefix with.
 *
 * @returns The unique ID.
 */

const uniqueId = /*#__PURE__*/(() => {
  let num = 0;
  return prefix => {
    prefix = prefix ? String(prefix) : '';
    num += 1;
    return prefix + num;
  };
})();
/**
 * Determines whether the passed value is a function.
 *
 * @param value - The value to check
 *
 * @returns true if value is a function, false otherwise.
 */

const isFunction = value => Boolean(value && value.constructor && value.call && value.apply);
/**
 * Casts the provided value as an array if it's not one.
 *
 * @param value The value to cast.
 *
 * @returns The cast array.
 */

const castArray = value => {
  return Array.isArray(value) ? value : [value];
};
/**
 * Creates an array of grouped elements based on the provided callback. The callback is invoked with the appropriate index values of each array for the current iteration.
 *
 * @param callback The function to iterate over each zipped pair.
 * @param arrOne The first array.
 * @param arrTwo The second array.
 *
 * @returns The new array of grouped elements.
 */

const zipWith = (callback, arrOne, arrTwo) => {
  if (!isFunction(callback)) return [];
  const a = castArray(arrOne);
  const b = castArray(arrTwo);
  const length = Math.max(a.length, b.length);
  if (length === 0) return [true];
  const res = [];

  for (let i = 0; i < length; i++) res[i] = callback(a[i], b[i]);

  return res;
};
/**
 * Returns an array containing boolean values of whether a given set of arrays have strict equality pairs.
 *
 * @param pairs The first set of pairs.
 * @param pairsTwo The second set of pairs
 *
 * @returns An array containing the boolean values.
 */

const pairsEq = (pairsA, pairsB) => zipWith((itemA, itemB) => itemA === itemB, pairsA, pairsB);

/**
 * Returns a normalized slug for the provided Prismic node.
 *
 * @remarks
 * The provided `node` must have a `slug` or `slugs` field or a
 * `uid` field. Will prioritize `slug` or `slugs`.
 *
 * @param node The node from Prismic to query.
 * @returns The normalized slug for the provided node.
 *
 * @example
 * const homeNode = { uid: 'home' }
 * getNormalizedSlug(homeNode)
 * // => null
 *
 * const pageNode = { uid: 'about-us' }
 * getNormalizedSlug(pageNode)
 * // => 'about-us'
 *
 * const blogNode = { slug: 'blog-slug', uid: 'blog' }
 * getNormalizedSlug(blogNode)
 * // => 'blog-slug'
 */

const getNormalizedSlug = node => {
  var _node$slugs, _node$slugs2;

  // Always prefer slug if available.
  if (node === null || node === void 0 ? void 0 : node.slug) return normalizeUID(node === null || node === void 0 ? void 0 : node.slug); // If multiple slugs are present, use the first (most recent).

  if (node === null || node === void 0 ? void 0 : (_node$slugs = node.slugs) === null || _node$slugs === void 0 ? void 0 : _node$slugs.length) return normalizeUID(node === null || node === void 0 ? void 0 : (_node$slugs2 = node.slugs) === null || _node$slugs2 === void 0 ? void 0 : _node$slugs2[0]); // Fallback to UID if no slug available.

  if (node === null || node === void 0 ? void 0 : node.uid) return getNormalizedUID(node);
  return null;
};

/**
 * Returrns a normalized UID for a given Prismic node.
 *
 * @remarks
 * The provided `node` must have a `uid` field.
 *
 * @param node The node from Prismic to query.
 *
 * @returns The normalized UID for the provided node.
 */

const getNormalizedUID = node => {
  var _node$slugs;

  // Always prefer UID if available.
  if (node === null || node === void 0 ? void 0 : node.uid) return normalizeUID(node === null || node === void 0 ? void 0 : node.uid); // Fallback to slug if no UID available.

  if ((node === null || node === void 0 ? void 0 : node.slug) || (node === null || node === void 0 ? void 0 : (_node$slugs = node.slugs) === null || _node$slugs === void 0 ? void 0 : _node$slugs.length)) return getNormalizedSlug(node);
  return null;
};

/**
 * Gets the parts of a full URL path as an array for the provided Prismic `node`.
 *
 * @remarks
 * The node must have a `uid` field.
 *
 * @param node The node from Prismic to query.
 * @returns An array containing the parts of a full URL path for the given `node`.
 *
 * @example
 * arrayPathFor(node)
 * // => ['news', 'news-post-title']
 *
 */

const arrayPathFor = node => {
  var _node$data, _node$data$parent;

  const normalizedUID = getNormalizedUID(node);
  const parent = node === null || node === void 0 ? void 0 : (_node$data = node.data) === null || _node$data === void 0 ? void 0 : (_node$data$parent = _node$data.parent) === null || _node$data$parent === void 0 ? void 0 : _node$data$parent.document;
  if (parent === null || parent === void 0 ? void 0 : parent.uid) // We cast to string[] since filter will remove any `null` values.
    return [arrayPathFor(parent), normalizedUID].flat().filter(Boolean);
  return normalizedUID ? [normalizedUID] : [];
};

/**
 * Returns an array containing the parent structure for a given Prismic node.
 * Each item in the array represents a parent node for the provided `node`.
 *
 * @param node The node to get the breadcrumbs for.
 * @returns An array with the parent Prismic nodes for the given `node`.
 *
 * @example
 * breadcrumbNodes(node)
 * // => [{ uid: 'news', data: { ... } }, { uid: 'company-updates', data: { ... } }]
 */
const breadcrumbNodes = node => {
  var _node$data, _node$data$parent;

  const parent = node === null || node === void 0 ? void 0 : (_node$data = node.data) === null || _node$data === void 0 ? void 0 : (_node$data$parent = _node$data.parent) === null || _node$data$parent === void 0 ? void 0 : _node$data$parent.document;
  if (parent) return [breadcrumbNodes(parent), node].flat();
  return [node];
};

/**
 * Creates a `FormData` object containing key-value data from the provided `data` object.
 *
 * @param data Data object to convert into a `FormData` object.
 * @returns The created `FormData` object.
 *
 * @example
 * const formDataObj = createFormData({ name: 'Alex', email: 'alex@gmail.com' })
 * formDataObj.toString()
 * // => [object FormData]
 */
const createFormData = data => {
  const formData = new FormData();
  const keys = Object.keys(data);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    formData.append(key, data[key]);
  }

  return formData;
};

/**
 * Returns the first non-null value in an array given an `index` searching downward.
 *
 * @param index The index to start from.
 * @param arr The array to search.
 * @returns The first non-null value or `undefined`
 *
 * @example
 * const array = [null, undefined, 'foo', 'bar']
 *
 * firstLeft(4, array)
 * // => 'bar'
 *
 * firstLeft(3, array)
 * // => 'foo'
 *
 * firstLeft(2, array)
 * // => undefined
 */

const firstLeft = (index, arr) => {
  arr = castArray(arr);
  if (index >= arr.length) index = arr.length - 1;

  while (index > -1 && (arr[index] === null || arr[index] === undefined)) index--;

  return arr[index];
};

/**
 * Retrieves the fluid data for an image object generated by localFile `gatsby-image`.
 *
 * @param image The processed `image` object to query.
 * @returns The fluid object for the provided `image`.
 *
 * @example
 * getImageFluid(data?.primary?.image)
 * // => { aspectRatio: 0.772313, src: '...', srcSet: '...' }
 */
const getImageFluid = image => {
  var _image$localFile, _image$localFile$chil;

  return image === null || image === void 0 ? void 0 : (_image$localFile = image.localFile) === null || _image$localFile === void 0 ? void 0 : (_image$localFile$chil = _image$localFile.childImageSharp) === null || _image$localFile$chil === void 0 ? void 0 : _image$localFile$chil.fluid;
};

/**
 * Safely retrieves data from a Prismic rich text field.
 *
 * @param obj The Prismic Rich Text object to query.
 * @returns The HTML string of the provided rich text field.
 *
 * @example
 * getRichText(data?.primary?.intro)
 * // => '<h1>About us</h1><p>Lorem ipsum sit dolor.</p>
 */
const getRichText = obj => (obj === null || obj === void 0 ? void 0 : obj.text) ? obj === null || obj === void 0 ? void 0 : obj.html : undefined;

/**
 * Gets all tags for the provided Prismic node.
 *
 * @param node The node to query.
 * @returns An array containing all the tags for the node.
 *
 * @example
 * const node = {
 *  tags: ['blog post', 'company updates']
 *  ...otherData
 * }
 *
 * getTags(node)
 * // => ['blog post', 'company updates']
 */
const getTags = node => node === null || node === void 0 ? void 0 : node.tags;

/**
 * Checks if the given `tag` exists as a tag on a Prismic node. This lookup is case-insensitive.
 *
 * @param tag The tag to check.
 * @param node The node to query.
 * @returns `true` if tag is a tag on `node`, `false` otherwise.
 *
 * @example
 * const node = {
 *  tags: ['blog post', 'company updates']
 * }
 *
 * hasTag('blog post', node)
 * // => true
 *
 * hasTag('Blog Post', node)
 * // => true
 *
 * hasTag('team member', node)
 * // => false
 */

const hasTag = (tag, node) => getTags(node).some(t => t.toLowerCase() === tag.toLowerCase());

/**
 * Constant that will be `true` when run in the browser, and `false` on the server.
 * Useful for preventing breaking static builds when you need to run DOM specific
 * code.
 *
 * @example
 * // This function is now safe to run server-side.
 * const ssrSafeFunction = () => {
 *  if (!isBrowser) return
 *
 *  const body = document.querySelector('body')
 * }
 *
 * // This will break static builds in Gatsby or other
 * // similar tools.
 * const unSsrSafeFunction = () => {
 *  const body = document.querySelector('body')
 * }
 */
const isBrowser = typeof window !== 'undefined';

/**
 * Checks if the provided string is an internal link. A link is considered to be internal
 * if it does not include an `http` prefix.
 *
 * @param url The string to check if it is an internal link.
 * @returns `true` if the link is internal, `false` otherwise.
 *
 * @example
 * isInternal('/internal-link')
 * // => true
 *
 * isInternal('https://external.com')
 * // => false
 *
 * isInternal('//external.com')
 * // => false
 */
const isInternal = url => /^\/(?!\/)/.test(url);

/**
 * Checks to see if a path is a child of the current location.
 *
 * @param path Path to check.
 * @param location Location object.
 * @returns `true` if `path` is a child of the current location, `false` otherwise.
 *
 * @example
 * const location = { ...window.location, pathname: '/path/' }
 * isPathActive('/path', location)
 * // => true
 *
 * const differentLocation = { ...window.location, pathname: '/different-path/' }
 * isPathActive('/path', differentLocation)
 * // => false
 *
 * const childLocation = { ...window.location, pathname: '/path/child/' }
 * isPathActive('/path', location)
 * // => true
 */

const isPathActive = (path, location) => {
  const sanitizedPath = sanitizePath(path);
  const sanitizedLocationPath = sanitizePath((location === null || location === void 0 ? void 0 : location.pathname) || '');
  if (!sanitizedPath.length || !sanitizedLocationPath.length) return false;
  return sanitizedLocationPath.startsWith(sanitizedPath);
};

/**
 * Checks to see if an exact path is the current location.
 *
 * @param path Path to check.
 * @param location Location object.
 * @returns `true` if the `path` is active, `false` otherwise.
 *
 * @example
 * const location = { ...window.location, pathname: '/path/' }
 * isPathActiveExact('/path', location)
 * // => true
 *
 * const differentLocation = { ...window.location, pathname: '/different-path/' }
 * isPathActiveExact('/path', differentLocation)
 * // => false
 *
 * const childLocation = { ...window.location, pathname: '/path/child/' }
 * isPathActiveExact('/path', location)
 * // => false
 */

const isPathActiveExact = (path, location) => {
  const sanitizedPath = sanitizePath(path);
  const sanitizedLocationPath = sanitizePath((location === null || location === void 0 ? void 0 : location.pathname) || '');
  return sanitizedPath === sanitizedLocationPath;
};

/**
 * Gets the parts of a full URL path (excluding the host) as an array for a given
 * Prismic node. The node must have a `slug` field. This is a specialized function
 * for Prismic's `linkResolver` function.
 *
 * @param node The node from Prismic to query.
 * @param isGraphQLNode Used to specify if this `PrismicNode` is from Gatsby's
 * GraphQL data layer.
 * @returns The array containing the parts of a full URL path for the given `node`.
 *
 * @example
 * const homeNode = { uid: 'home', ...data }
 * linkResolverArrayPathFor(homeNode)
 * // => []
 *
 * const careersNode = { uid: 'careers', ...data }
 * linkResolverArrayPathFor(homeNode)
 * // => ['careers']
 *
 * const childNode = { uid: 'child', data: { parent: { uid: 'parent' }}}
 * linkResolverArrayPathFor(childNode)
 * // => ['parent', 'child']
 */

const linkResolverArrayPathFor = (node, isGraphQLNode = false) => {
  var _node$data, _parent;

  let parent = node === null || node === void 0 ? void 0 : (_node$data = node.data) === null || _node$data === void 0 ? void 0 : _node$data.parent;
  if ((_parent = parent) === null || _parent === void 0 ? void 0 : _parent.value) isGraphQLNode = true;

  if (isGraphQLNode) {
    var _parent2, _parent2$value;

    const normalizedSlug = getNormalizedSlug(node);
    parent = (_parent2 = parent) === null || _parent2 === void 0 ? void 0 : (_parent2$value = _parent2.value) === null || _parent2$value === void 0 ? void 0 : _parent2$value.document;
    if (parent) return [linkResolverArrayPathFor(parent, isGraphQLNode), normalizedSlug].flat() // We cast to string[] since filter will remove any `null` values.
    .filter(Boolean);
    return normalizedSlug ? [normalizedSlug] : [];
  } else {
    const normalizedUID = getNormalizedUID(node);
    if (parent) return [linkResolverArrayPathFor(parent, isGraphQLNode), normalizedUID].flat() // We cast to string[] since filter will remove any `null` values.
    .filter(Boolean);
    return normalizedUID ? [normalizedUID] : [];
  }
};

/**
 * Returns the full URL path (excluding hostname) for a given Prismic `node`.
 * The computed path will contain leading and trailing forward slashes.
 *
 * @remarks
 * The provided `node` must have a `uid` field. This is a specialized function
 * for Prismic's `linkResolver` function.
 *
 * @param node The node from Prismic to process.
 * @returns A string containing the full URL path for the `node`.
 *
 * @example
 * const homeNode = { uid: 'home', ...data }
 * linkResolver(homeNode)
 * // => '/'
 *
 * const careersNode = { uid: 'careers', ...data }
 * linkResolver(homeNode)
 * // => '/careers/'
 *
 * const childNode = { uid: 'child', data: { parent: { uid: 'parent' }}}
 * linkResolver(childNode)
 * // => /parent/child/
 */

const linkResolverPathFor = node => [null, ...linkResolverArrayPathFor(node), null].join('/');

/**
 * Removes any falsey key-value pairs from an object.
 * @private
 *
 * @param obj The object to remove falsey values from.
 *
 * @returns A new object with all falsey key-value pairs removed.
 */

const _removeFalseyObjValues = obj => {
  const result = {};

  for (const key in obj) {
    if (!obj[key]) continue;
    result[key] = obj[key];
  }

  return result;
};
/**
 * Returns normalized values that can be readily serialized into a `FormData` object.
 * Arrays and objects will be stringified, and objects are optionally compacted based on
 * the `removeFalseyValues` parameter.
 *
 * @remarks The `removeFalseyValues` parameter is primarily used to handle multi-select
 * checkbox fields. If your form has a multi-select checkbox field setting
 * `removeFalseyValues` to true will cause the final normalized object to only contain
 * the keys of the selected checkboxes, and not the false ones.
 *
 * @param object The data to normalize.
 * @param removeFalseyValues Whether to remove falsey values in from JSON values `values`.
 * @returns The normalized values.
 *
 * @example
 * const formData = {
 *  checkboxes: {
 *    one: true,
 *    two: false,
 *  },
 *  array: ['one', 'two'],
 *  name: 'Alex'
 * }
 *
 * normalizeFormData(formData)
 * // {
 * //   checkBoxes: '{"one":true,"two":false}',
 * //   array: 'one,two',
 * //   name: 'Alex'
 * // }
 *
 * normalizeFormData(formData, true)
 * // {
 * //   checkBoxes: 'one',
 * //   array: 'one,two',
 * //   name: 'Alex'
 * // }
 */


const normalizeFormData = (object, removeFalseyValues = false) => {
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      // TODO: Casting to any here is a pretty bandaid solution. Works, but maybe could be better.
      // This is strange because FormData is able to serialize File arrays normally.
      if (value[0] instanceof File) acc[key] = value;else acc[key] = value.toString();
      return acc;
    } // This value is a probably a multi-select checkbox field.


    if (isPlainObject(value)) {
      if (removeFalseyValues) acc[key] = Object.keys(_removeFalseyObjValues(value)).toString();else acc[key] = JSON.stringify(value);
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

/**
 * Returns the full URL path (excluding hostname) for a given Prismic `node`. The computed path will contain leading and trailing forward slashes.
 *
 * @remarks
 * The provided `node` must have a `uid` field.
 *
 * @param node The node from Prismic to process.
 * @returns A string containing the full URL path for the `node`.
 *
 * const homeNode = { uid: 'home', ...data }
 * pathFor(homeNode)
 * // => '/'
 *
 * const careersNode = { uid: 'careers', ...data }
 * pathFor(careersNode)
 * // => '/careers/'
 *
 * const childNode = { uid: 'child', data: { parent: { document: { uid: 'parent' }}} }
 * pathFor(childNode)
 * // => '/parent/child/'
 */

const pathFor = node => [null, ...arrayPathFor(node), null].join('/');

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

// A type of promise-like that resolves synchronously and supports only one observer

const _iteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.iterator || (Symbol.iterator = Symbol("Symbol.iterator"))) : "@@iterator";

const _asyncIteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.asyncIterator || (Symbol.asyncIterator = Symbol("Symbol.asyncIterator"))) : "@@asyncIterator";

// Asynchronously call a function and send errors to recovery continuation
function _catch(body, recover) {
	try {
		var result = body();
	} catch(e) {
		return recover(e);
	}
	if (result && result.then) {
		return result.then(void 0, recover);
	}
	return result;
}

/**
 * Posts a form to Netlify with the provided `formName` and `values` object. If `recaptchaResponse` is provided, the response is added to the value with the correct key.
 *
 * @param formName The name of the form for Netlify.
 * @param values The form data object to encode.
 * @param recaptchaResponse The recaptcha response.
 * @returns `true` if successful, `false` otherwise.
 *
 * @example
 * // Some form submission handler
 * const handleSubmit = async (event) => {
 *  event.preventDefault()
 *
 *  // `createFormData` is another helper from this package.
 *  await postNetlifyForm(createFormData('form-name', { name: 'Alex' }))
 * }
 */

const postNetlifyForm = function postNetlifyForm(formName, values, recaptchaResponse) {
  try {
    return Promise.resolve(_catch(function () {
      const formDataObj = _extends({
        'form-name': formName
      }, values);

      if (recaptchaResponse) formDataObj['g-recaptcha-response'] = recaptchaResponse;
      return Promise.resolve(fetch('/', {
        method: 'post',
        body: createFormData(formDataObj)
      })).then(function (response) {
        const status = (response === null || response === void 0 ? void 0 : response.status) || 0;
        return status >= 200 && status < 300;
      });
    }, function (error) {
      console.error(error);
      return false;
    }));
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * Returns an array of boolean values by comparing values at the specified `key`. If the value at `key` is an array, the array will be run through a strict comparison iteratee to compute the boolean array.
 *
 * @param key The key in each object to compare.
 * @param obj1 The first object to query.
 * @param obj2 The second object to query.
 * @returns The array of boolean values.
 *
 * const obj1 = {
 *  color: ['red', 'blue']
 * }
 * const obj2 = {
 *  color: ['red', 'blue']
 * }
 *
 * propPairsEq('color', obj1, obj2)
 * // => [true, true]
 *
 * const obj3 = {
 *  color: ['red', 'green']
 * }
 *
 * propPairsEq('color', obj1, obj3)
 * // => [true, false]
 */

const propPairsEq = (key, obj1, obj2) => {
  if (obj1 === undefined && obj2 === undefined) return [false];
  return pairsEq(obj1 === null || obj1 === void 0 ? void 0 : obj1[key], obj2 === null || obj2 === void 0 ? void 0 : obj2[key]);
};

/**
 * Checks if the provided value is empty.
 *
 * @param value The value to check.
 * @returns `undefined` if `value` is empty. Returns `value` otherwise.
 *
 * @example
 *
 * undefIfEmpty('')
 * // => undefined
 *
 * undefIfEmpty([])
 * // => undefined
 *
 * undefIfEmpty('foo')
 * // => 'foo'
 */
const undefIfEmpty = value => {
  const t = typeof value === 'object' && value !== null ? Object.keys(value) : value;
  return (t === null || t === void 0 ? void 0 : t.length) ? value : undefined;
};

/**
 * Recursively accumulates a list of values from an input.
 *
 * @remarks
 * Only object values, not their keys, are included in the result.
 *
 * @param input The input with values.
 * @param initialValues The base list of values to which accumulated values will be added.
 * @returns An array of the input's values gathered recursively.
 *
 * @example
 * valuesDeep({ names: ['Foo', 'Bar'], opts: { last: false } })
 * // => ['Foo', 'Bar', false]
 *
 */

const valuesDeep = (input, initialValues = []) => {
  if (isPlainObject(input)) return [...initialValues, ...Object.values(input).map(val => valuesDeep(val, initialValues))].flat();
  if (Array.isArray(input)) return [...initialValues, ...input.map(val => valuesDeep(val, initialValues))].flat();
  return [...initialValues, input];
};

/**
 * Returns a unique identifier for use with `zipAdjSlices`.
 * @private
 *
 * @param prefix Optional prefix for the ID.
 * @returns A unique ID.
 */

const _zipAdjSlicesGenUniqueId = prefix => uniqueId(prefix);
/**
 * Create a modified list of slices by grouping a collection of slice types into a single slice of a given name.
 *
 * @param zipType The name of the new grouped slice.
 * @param types The types of slices to group.
 * @param slices Array of slices to process.
 * @returns The new modified slice list array.
 *
 * @example
 *
 * const slices = [
 *  { __typename: 'PageBodyHeader', data: {} },
 *  { __typename: 'PageBodyText', data: {} },
 *  { __typename: 'PageBodyImages', data: {} },
 *  { __typename: 'PageBodyText', data: {} },
 *  { __typename: 'PageBodyFooter', data: {} },
 * ]
 *
 * const newSlices = zipAdjSlices(
 *  'PageBodyArticle',
 *  ['PageBodyText', 'PageBodyImages'],
 *  slices
 * )
 *
 * // Returns a new aray like:
 * [
 *  { __typename: 'PageBodyHeader', data: {} }
 *  {
 *    __typename: 'PageBodyArticle',
 *    data: {
 *      slices: [
 *        { __typename: 'PageBodyText', data: {} },
 *        { __typename: 'PageBodyImages', data: {} },
 *        { __typename: 'PageBodyText', data: {} },
 *      ]
 *    }
 *  }
 *  { __typename: 'PageBodyFooter', data: {} }
 * ]
 */


const zipAdjSlices = (zipType, types = [], slices = []) => {
  const isSpecialSlice = slice => types.includes(slice === null || slice === void 0 ? void 0 : slice.__typename);

  return [...slices].reduce((acc, curr, idx) => {
    const prev = slices[idx - 1]; // If this is a special slice and the previous was not a special slice,
    // assume the start of a zipAdjSlices slice.

    if (isSpecialSlice(curr) && !isSpecialSlice(prev)) {
      return [...acc, {
        __typename: zipType,
        id: _zipAdjSlicesGenUniqueId(zipType),
        slices: [curr]
      }];
    } // If this is a special slice, append it to the most recent zipAdjSlices.
    // We can assume the last slice in acc is a zipAdjSlices due to the
    // previous code block.


    if (isSpecialSlice(curr)) {
      // We can assert here since slices wont be undefined
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acc[acc.length - 1].slices = [...acc[acc.length - 1].slices, curr];
      return acc;
    } // If this is not a special slice, append the slice as normal.


    return [...acc, curr];
  }, []);
};

/**
 * Returns a unique identifier for use with `zipBetweenSlices`.
 * @private
 *
 * @param prefix Optional prefix for the ID.
 *
 * @returns A unique ID.
 */

const _zipBetweenSlicesGenUniqueId = prefix => uniqueId(prefix);
/**
 * Create a modified list of slices by grouping all slices between the specified  `type` into a single slice.
 *
 * @param zipType The name of the new slice to create.
 * @param type The "wrapping" slice type.
 * @param slices Array of slices to process.
 * @returns The new modified slice list array.
 *
 * @example
 *
 * const slices = [
 *  { __typename: 'PageBodyWrapper', data: {} },
 *  { __typename: 'PageBodyText', data: {} },
 *  { __typename: 'PageBodyImages', data: {} },
 *  { __typename: 'PageBodyWrapper', data: {} },
 * ]
 *
 * const newSlices = zipAdjSlices(
 *  'PageBodyArticle',
 *  'PageBodyWrapper',
 *  slices
 * )
 *
 * // Returns a new aray like:
 * [
 *  {
 *    __typename: 'PageBodyWrapper',
 *    data: {
 *      slices: [
 *        { __typename: 'PageBodyText', data: {} },
 *        { __typename: 'PageBodyImages', data: {} },
 *      ],
 *      startingSlice: { __typename: 'PageBodyText', data: {} }
 *      endingSlice: { __typename: 'PageBodyImages', data: {} }
 *    }
 *  }
 * ]
 */


const zipBetweenSlices = (zipType, type, slices = []) => {
  let isZipping = false;
  return slices.reduce((acc, curr) => {
    const lastAccSlice = acc[acc.length - 1];
    const isSpecialSlice = (curr === null || curr === void 0 ? void 0 : curr.__typename) === type;
    const isBetweenSlices = (lastAccSlice === null || lastAccSlice === void 0 ? void 0 : lastAccSlice.__typename) === zipType;

    if (isSpecialSlice && !isBetweenSlices) {
      isZipping = true;
      return [...acc, {
        __typename: zipType,
        id: _zipBetweenSlicesGenUniqueId(zipType),
        slices: [],
        startingSlice: curr
      }];
    }

    if (isSpecialSlice && isBetweenSlices) {
      isZipping = false;
      lastAccSlice.endingSlice = curr;
      return acc;
    }

    if (isBetweenSlices && isZipping) {
      // We can assert here since slices wont be undefined
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastAccSlice.slices = [...lastAccSlice.slices, curr];
      return acc;
    }

    return [...acc, curr];
  }, []);
};

const ANCHOR_REGEX = /#\S*$/;
/**
 * Extracts the anchor from a URL if present.
 *
 * @param url The URL from which to extract an anchor.
 *
 * @returns The URL's anchor if present, `undefined` otherwise.
 *
 * @example
 * extractAnchor('https://#top') // #top
 * extractAnchor('https://example.com#top') // #top
 * extractAnchor('https://example.com') // undefined
 */

const extractAnchor = url => {
  var _url$match;

  return (_url$match = url.match(ANCHOR_REGEX)) === null || _url$match === void 0 ? void 0 : _url$match[0];
};

/**
 * Determines if a URL is an anchor link.
 *
 * @param url The URL to test.
 *
 * @returns `true` if the URL is an anchor link, `false` otherwise.
 *
 * @example
 * isAnchorOnly('https://#top') // true
 * isAnchorOnly('https://example.com') // false
 * isAnchorOnly('https://example.com#top') // false
 */
const isAnchorOnly = url => /^https?:\/\/#/.test(url);

export { arrayPathFor, breadcrumbNodes, createFormData, extractAnchor, firstLeft, getImageFluid, getNormalizedSlug, getNormalizedUID, getRichText, getTags, hasTag, isAnchorOnly, isBrowser, isInternal, isPathActive, isPathActiveExact, linkResolverArrayPathFor, linkResolverPathFor, normalizeFormData, pathFor, postNetlifyForm, propPairsEq, undefIfEmpty, valuesDeep, zipAdjSlices, zipBetweenSlices };
//# sourceMappingURL=helpers.esm.js.map
